
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import axios from "@/axios-instance"
import moment from "moment"
import Permission from "@/core/config/Permission";
import { debounce } from "debounce";
import { saveAs } from 'file-saver';
import Scholarship from "./procedureSettings/scholarship.vue";
import ApplicantQuickDrawer from "@/components/customers/ApplicantQuickDrawer.vue";
import { exportStyledXlsx, formatMoneyVN, XlsxColumn } from "@/core/helpers/exportXlsx";
import { printUuDaiHocBong } from "@/core/helpers/printUuDaiHocBong";
import { canViewCandidateDetail } from "@/core/helpers/profileAccess";
interface ICustomer {
    id: string;
    studentId: string
    fullName: string;
    major: string;
    idNumber: string;

    phase: number;
    code: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    studentCode: string;
    lastModifiedBy: string;
    isApplicationFormPrinted: string;
}
interface IPagination {
    currentPage: number;
    total: number;
    limit: number;
}
// Xuất Excel Danh sách nhập học (FE styled — mẫu promotion.vue): width/align/wrap + tập cột TIỀN theo key của header
const APPLICANT_XLSX_COL_SPEC: Record<string, Partial<XlsxColumn>> = {
    createdAt: { width: 11, align: "C" },
    phase: { width: 7, align: "C" },
    updatedAt: { width: 13, align: "C" },
    studentCode: { width: 12 },
    fullName: { width: 26, wrap: true },
    contactMobile: { width: 12 },
    contactEmail: { width: 28 },
    email: { width: 28 },
    idNumber: { width: 14 },
    highschoolName: { width: 28, wrap: true },
    highschoolProvince: { width: 16 },
    permanentAddress: { width: 30, wrap: true },
    permanentWard: { width: 22 },
    permanentProvince: { width: 22 },
    adMajorName: { width: 26, wrap: true },
    programtype: { width: 12, align: "C" },
    status: { width: 13, align: "C" },
    isApplicationFormPrinted: { width: 10, align: "C" },
    open: { width: 13, align: "C" },
    lastModifiedBy: { width: 14 },
    assignee: { width: 20 },
    hasDeposit: { width: 10, align: "C" },
    hasTuition: { width: 10, align: "C" },
    tuitionRegistration: { width: 18, wrap: true },
    tuition: { width: 14, align: "R" },
    scholarships: { width: 26, wrap: true },
    scholarshipsA: { width: 14, align: "R" },
    svsl: { width: 20, wrap: true },
    svslA: { width: 13, align: "R" },
    promotion: { width: 46, wrap: true },
    promotionA: { width: 14, align: "R" },
    totalReduction: { width: 14, align: "R" },
    totalpaid: { width: 14, align: "R" },
    totalremaining: { width: 13, align: "R" },
}
const APPLICANT_XLSX_MONEY_KEYS = new Set(['tuition', 'scholarshipsA', 'svslA', 'promotionA', 'totalReduction', 'totalpaid', 'totalremaining'])

//Tình trạng MẶC ĐỊNH của bộ lọc: 3 trạng thái đang nhập học, KHÔNG lấy hồ sơ đã hủy
//(muốn xem hồ sơ hủy thì tự chọn 'Đã hủy' trong ô Tình trạng).
//Dùng chung cho: giá trị khởi tạo, nút "Đặt lại", và bỏ lọc bằng chip thống kê.
const DEFAULT_STATUS = ['confirmed', 'completed', 'processing']
function buildApplicantXlsxColumns(header: Record<string, string>): XlsxColumn[] {
    return Object.keys(header).map((k) => ({ label: header[k], width: 14, ...APPLICANT_XLSX_COL_SPEC[k] }))
}
// "Còn lại" fallback khi applicants.totalremaining trong DB là null/NaN (hồ sơ chưa qua luồng phiếu thu/fix tool).
// Tính đúng công thức BE _updateTotalpaid_Applicant (applicant.service.js):
// total = Σ(items type 'tuition') − Σ(items miễn giảm ≠ tuition/tuitionpaid/deposit); paid = Σ(tuitionpaid) + Σ(deposit)
function calcRemainingFallback(item: any) {
    if (item.status == 'cancel') return ''
    const orderItems = item.orderDetails?.items
    if (!Array.isArray(orderItems) || !orderItems.length) return ''
    const sum = (arr: any[]) => arr.reduce((a, b) => a + +b.value, 0)
    const tuition = sum(orderItems.filter(i => i.type == 'tuition'))
    if (!tuition) return '' // chưa chọn gói học phí — BE cũng skip trường hợp này
    const nontuition = sum(orderItems.filter(i => !['tuition', 'tuitionpaid', 'deposit'].includes(i.type)))
    const paid = sum(orderItems.filter(i => i.type == 'tuitionpaid' || i.type == 'deposit'))
    return tuition - nontuition - paid
}
export default defineComponent({
    name: "customers-listing",
    components: {
        Datatable,
        ApplicantQuickDrawer,
    },
    data() {
        return {
            filter: {} as any,
            loading: false,
        }
    },
    watch: {
        search: debounce(async function (this) {
            this.searchItems()
        }, 300)
    },
    methods: {
        // Drawer xem nhanh phiếu nhập học: đã tách thành component ApplicantQuickDrawer (dùng chung với
        // tab Hồ sơ nhập học ở chi tiết ứng viên); row list đã chứa full applicant (…item spread) -> truyền thẳng
        openQuickView(customer: any) {
            (this.$refs.applicantQuickDrawer as any)?.open(customer);
        },
        money(v: any) {
            const n = Number(v);
            if (!v || isNaN(n) || n === 0) return "—";
            return n.toLocaleString("vi-VN") + " đ";
        },
        scoreLabel(v: any) {
            return (v === null || v === undefined || v === "") ? "—" : v;
        },
        programtypeLabel(t: any) {
            const map: any = { TIENG_VIET: "Tiếng Việt", SONG_NGU: "Song ngữ" };
            return map[t] || t || "";
        },
        englishLabel(t: any) {
            const map: any = {
                none: "Không kiểm tra xếp lớp",
                english_thpt: "Điểm Tiếng Anh THPT",
                umt_test: "Thi xếp lớp UMT",
                english_certificate: "Chứng chỉ Tiếng Anh",
            };
            return map[t] || t || "—";
        },
        bhytLabel(t: any) {
            const map: any = { BHYT15: "BHYT 15 tháng", BHYT12: "BHYT 12 tháng", other: "Không đăng ký" };
            return map[t] || t || "—";
        },
        payLabel(code: any) {
            const map: any = { cash: "Tiền mặt", bank_transfers: "Chuyển khoản", atm_card: "ATM/VISA/MASTERCARD" };
            return map[code] || code || "—";
        },
        genderLabel(gender: any) {
            const map = { male: "Nam", female: "Nữ", Male: "Nam", Female: "Nữ" };
            return map[gender] || gender || "";
        },
        drawerAddress(row: any) {
            return [row.postalAddress, row.postalWard, row.postalDistrict, row.postalProvince]
                .filter(Boolean).join(", ");
        },
        permanentAddressText(row: any) {
            return [row.permanentAddress, row.permanentWard, row.permanentDistrict, row.permanentProvince]
                .filter(Boolean).join(", ");
        },
        assigneeLabel(row: any) {
            if (row.Student?.groupAssignee) return row.Student.Group?.groupName || "";
            if (row.Student?.assignee) return row.Student.User?.fullName || "";
            return "";
        },
        applicanttotalpaids() {
            Swal.fire({
                text: `Bạn có muốn fix data totalpaild cho sinh viên này không?`,
                icon: "question",
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: "Đồng ý",
                cancelButtonText: "Trở lại",
                customClass: {
                    confirmButton: "btn btn-primary order-2",
                    cancelButton: "btn btn-light order-1",
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    let urlApi = `admission/fix/applicanttotalpaids`
                    let temp = this.tableData.filter(i=> this.checkedCustomers.includes(i.id)).map(t=> t.studentCode) 
                    let tempData = {studentcodes: temp}
                  
                    axios.post(urlApi,tempData).then(({data}) => {
                        Swal.fire({
                            text: `Đã fix data thành công!`,
                            icon: "success",
                            buttonsStyling: false,
                            confirmButtonText: "Đóng",
                            customClass: {
                                confirmButton: "btn btn-primary",
                            },
                            }).then(() => {
                            Swal.close()
                            },300);
                        
                    })
                }
                
            });
        },
        // In phiếu thông tin sinh viên (browser render + autoprint — như nút In ở trang chi tiết)
        printApplicantInfo(customer: any) {
            window.open(`/phieuthongtinsinhvien?id=${customer.id}&autoprint=1`, '_blank')
        },
        // In phiếu xác nhận ưu đãi/học bổng — dùng chung helper với tab Hồ sơ nhập học/trang chi tiết
        async printUuDai(customer: any) {
            try {
                const { data: applicant } = await axios.get(`applicant/${customer.id}`)
                if (!applicant || !applicant.id) {
                    Swal.fire({ text: 'Không tải được phiếu nhập học.', icon: 'warning', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
                    return
                }
                const hocbong = Array.isArray(applicant.freeTuitionRegistration)
                    ? applicant.freeTuitionRegistration.find((i: any) => i.registrationType == 'scholarships') : null
                const uudai = applicant.orderDetails?.promotion
                if (!hocbong && !uudai) {
                    Swal.fire({ text: 'Hồ sơ chưa có ưu đãi hoặc học bổng để in phiếu.', icon: 'info', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
                    return
                }
                if (hocbong && uudai) {
                    // SweetAlert2 v9 KHÔNG có showDenyButton -> cancel làm nút "Ưu đãi", phân biệt ESC qua DismissReason.cancel
                    const res = await Swal.fire({
                        text: 'Hồ sơ có cả học bổng và ưu đãi — in phiếu nào?',
                        icon: 'question',
                        buttonsStyling: false,
                        showCancelButton: true,
                        showCloseButton: true,
                        confirmButtonText: 'In phiếu Học bổng',
                        cancelButtonText: 'In phiếu Ưu đãi',
                        customClass: { confirmButton: 'btn btn-primary order-2', cancelButton: 'btn btn-light-primary order-1' },
                    })
                    if (res.isConfirmed) await printUuDaiHocBong(applicant, hocbong)
                    else if (res.dismiss === Swal.DismissReason.cancel) await printUuDaiHocBong(applicant, uudai)
                    return
                }
                await printUuDaiHocBong(applicant, hocbong || uudai)
            } catch (e: any) {
                Swal.fire({ text: e?.response?.data?.message || 'Không tải được thông tin để in phiếu.', icon: 'error', buttonsStyling: false, confirmButtonText: 'Đóng', customClass: { confirmButton: 'btn btn-primary' } })
            }
        },
        //XÓA PHIẾU NHẬP HỌC (chỉ Superadmin) — xóa cứng phiếu, hồ sơ trở lại "chưa khởi tạo nhập học"
        //để có thể thu hồi QĐ trúng tuyển (xét sai) hoặc khởi tạo lại phiếu. Bắt buộc nhập lý do (ghi timeline).
        async deleteApplicant(customer: any) {
            const result = await Swal.fire({
                title: "Xóa phiếu nhập học?",
                html: `Phiếu <b>${customer.code || ""}</b> của <b>${customer.fullName || ""}</b> (hồ sơ ${customer.profileCode || "—"})`
                    + ` sẽ bị <b>xóa vĩnh viễn</b>.<br/>Hồ sơ trở lại trạng thái <b>chưa khởi tạo nhập học</b>`
                    + ` — sau đó có thể thu hồi QĐ trúng tuyển hoặc "Mở khóa hồ sơ" để khởi tạo lại.`,
                icon: "warning",
                input: "textarea",
                inputPlaceholder: "Lý do xóa phiếu (bắt buộc)...",
                inputValidator: (value: any) => (!value || !String(value).trim()) ? "Vui lòng nhập lý do xóa phiếu" : null,
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: "Xóa phiếu",
                cancelButtonText: "Trở lại",
                customClass: {
                    confirmButton: "btn btn-danger order-2",
                    cancelButton: "btn btn-light order-1",
                },
            });
            if (!result.isConfirmed) return;
            try {
                //axios.delete: body phải đặt trong { data } (BE bắt buộc có reason để ghi [TIMELINE])
                const { data } = await axios.delete(`applicant/${customer.id}`, { data: { reason: String(result.value).trim() } });
                await Swal.fire({
                    text: data?.message || "Đã xóa phiếu nhập học.",
                    icon: "success",
                    buttonsStyling: false,
                    confirmButtonText: "Đóng",
                    customClass: { confirmButton: "btn btn-primary" },
                });
                await this.getData();
            } catch (e: any) {
                Swal.fire({
                    text: e?.response?.data?.errorMessage || e?.response?.data?.message || "Không xóa được phiếu nhập học.",
                    icon: "error",
                    buttonsStyling: false,
                    confirmButtonText: "Đóng",
                    customClass: { confirmButton: "btn btn-primary" },
                });
            }
        },
        sendMailStudent(id?: any) {
            Swal.fire({
                text: `Bạn có muốn gửi email thông báo hoàn tất nhập học lại cho sinh viên này không?`,
                icon: "question",
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: "Đồng ý",
                cancelButtonText: "Trở lại",
                customClass: {
                    confirmButton: "btn btn-primary order-2",
                    cancelButton: "btn btn-light order-1",
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    let urlApi = `applicant/mail/sendmailhoantatnhaphoc`
                    let tempData = {applicantids: [id]}
                    if(!id) {
                        tempData = {applicantids: this.checkedCustomers}
                    }
                    axios.post(urlApi,tempData).then(({data}) => {
                        // if(data.status == 'success') {
                        //     Swal.fire({
                        //         text: `Đã gửi mail thành công!`,
                        //         icon: "success",
                        //         buttonsStyling: false,
                        //         confirmButtonText: "Đóng",
                        //         customClass: {
                        //             confirmButton: "btn btn-primary",
                        //         },
                        //         }).then(() => {
                        //         Swal.close()
                        //         },300);
                        // }
                        
                    })
                }
                
            });
        },
        async resetFilter() {
            this.formData = {
                isApproved: -1,
                paid: -1,
                phase: -1,
                status: [...DEFAULT_STATUS],
                assignee: -1,
                scholarship: [],

            }
            //Đặt lại = về MẶC ĐỊNH (vẫn ẩn hồ sơ đã hủy), không phải đổ lại toàn bộ danh sách
            this.applyFilter()
        },
        //bấm chip thống kê = lọc nhanh theo tình trạng đó (ghi thẳng vào ô "Tình trạng" để 2 nơi luôn khớp);
        //bấm lại chip đang chọn = bỏ lọc
        toggleStatusChip(key: string) {
            const cur = this.formData.status || []
            //bấm lại chip đang chọn = về mặc định 3 trạng thái (không mở ra cả hồ sơ đã hủy)
            this.formData.status = (cur.length === 1 && cur[0] === key) ? [...DEFAULT_STATUS] : [key]
            this.applyFilter()
        },
        async applyFilter() {
            let results =  JSON.parse(JSON.stringify(this.initCustomers));
            // console.log('this.formData.scholarship',this.formData.scholarship)
            results = results.filter((item: any)=> 
                (this.formData.phase == -1 || (item.phase == this.formData.phase)) && 
                (this.formData.isApproved == -1 || ((item.isApplicationFormPrinted!="")  == (this.formData.isApproved == 1))) && 
                (this.formData.paid == -1 || (item.isDeposit && this.formData.paid == 2) || (this.formData.paid == 1 && item.isTuition)) && 
                (this.formData.assignee == -1 || (item.Student.assignee == this.formData.assignee || item.Student.groupAssignee == this.formData.assignee)) && 
                (this.formData.scholarship.length == 0 || this.formData.scholarship.includes(-1) || this.formData.scholarship.includes(item.scholarshipCode)) &&
                (this.formData.status.length == 0  || (this.formData.status.includes(item.status))))
            this.tableData.splice(0, this.tableData.length, ...results);
            this.initCustomersFIlter = results
            // console.log('initCustomersFIlter',this.initCustomersFIlter)
            // console.log('tableData',this.tableData)
            
        },
        popupLoading() {
            Swal.fire({
                html:"<div class='custom-loading'><span>Đang xử lý. Vui lòng chờ trong giây lát!!!</span> <span class='loader'></span></div>",
                buttonsStyling: false,
                timerProgressBar: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                showConfirmButton: false,
            }).then(() => {
                //abcdefgh
            });
        },
        moment: function (date) {
            return date?moment(date).format('DD/MM/YYYY'):''
        },
        async exportFileBase64() {
            this.popupLoading()
            await axios.get(`/applicant/avatars/download`,
            {responseType: 'arraybuffer', }) //arraybuffer, blob
            .then((response) => {
            const blob = new Blob([response.data], {
                type: 'application/octet-stream'
            })
            const url = URL.createObjectURL(blob)
            let cd = response.headers['content-disposition'].split('"')
            const fileName = cd.length > 1 ? cd[1] : 'danhsachanhthe.zip'
            saveAs(url, fileName)
            }).catch(err=> {
                console.log('err',err)
            })
            Swal.close()
        },
        
        exportFileAdmin() {
            let data = this.tableData as any //await this.funcReturnDataInit()
            //Cột theo yêu cầu phòng TS (2026-07): thứ tự + nhãn cố định; SVSL gộp vào "Ưu đãi nhận"
            const header = {
                phase: "Đợt",
                studentCode: "MSSV",
                fullName: "Họ tên",
                idNumber: "CCCD",
                contactMobile: "Điện thoại",
                contactEmail: "Email cá nhân",
                email: "Email UMT",
                permanentAddress: "Địa chỉ thường trú",
                permanentProvince: "Tỉnh/TP thường trú",
                highschoolName: "Trường Lớp 12",
                highschoolProvince: "Tỉnh/TP Lớp 12",
                adMajorName: "Ngành nhập học",
                programtype: "Loại đào tạo",
                status: "Trạng thái",
                isApplicationFormPrinted: "TNHS",
                open: "Chuyển giao",
                createdAt: "Ngày tạo",
                updatedAt: "Ngày cập nhật",
                lastModifiedBy: "Sửa lần cuối",
                assignee: "Người phụ trách",
                hasDeposit: "Đóng PGS",
                hasTuition: "Đóng HP",
                tuitionRegistration: "Số HK đóng",
                tuition: "Học phí",
                scholarships: "Học bổng cấp",
                scholarshipsA: "Giảm trừ HB",
                promotion: "Ưu đãi nhận",
                promotionA: "Giảm trừ ưu đãi",
                totalReduction: "Tổng giảm trừ",
                totalpaid: "Số tiền đã đóng",
                totalremaining: "Số tiền còn lại",
            }
            let programType = {
                "TIENG_VIET":"Tiếng Việt",
                "SONG_NGU":"Song Ngữ",
            }
            try {
                const items = data.map(item => {
                            let freeTuitionRegistration = item.freeTuitionRegistration||[]
                            let orderDetails = item.orderDetails||{}
                            let scholarships = freeTuitionRegistration.find(i=> i.registrationType == 'scholarships')?.scholarships||{}
                            let scholarshipsA = [] as any

                            let promotion = orderDetails.promotion
                            let promotionAddition = orderDetails.promotionAddition
                            let arr_promotion = [] as any
                            //array promotion
                            let otherPromotions = orderDetails.otherPromotions
                            let promotionMulti = orderDetails.promotionMulti
                            let svsl = null as any
                            if(otherPromotions) {
                                arr_promotion = arr_promotion.concat(otherPromotions)
                            }
                            if(promotionMulti) {
                                arr_promotion = arr_promotion.concat(promotionMulti)
                            }
                            if(promotion) {
                                if(promotion.code == 'SVSL') {
                                    svsl = promotion
                                }
                                else {
                                    arr_promotion.push(promotion)
                                }
                            }
                            if(promotionAddition) {
                                arr_promotion.push(promotionAddition)
                            }
                            let promotionA = [] as any
                            let arr_scholarshipA = ['promotion','promotionExtend']
                            let arr_promotionA = ['promotionvip','promotionvippro']
                            freeTuitionRegistration.forEach(i=> {
                                if(i.registrationType == 'scholarshipPromotionMultiData') {
                                    let data = i[i.registrationType]
                                    scholarshipsA = scholarshipsA.concat(data)
                                }
                                if(arr_scholarshipA.includes(i.registrationType)) {
                                    let name = i[i.registrationType].name
                                    if(name) {
                                        scholarshipsA.push(i[i.registrationType])   
                                    }
                                }
                                if(arr_promotionA.includes(i.registrationType)) {
                                    let name = i[i.registrationType].name
                                    if(name) {
                                        promotionA.push(i[i.registrationType])   
                                    }
                                }
                            })
                            let totalPromotion = scholarshipsA.concat(arr_promotion.concat(promotionA))||[]
                            // console.log('totalPromotion',totalPromotion)
                            // totalPromotion.forEach(i=>{
                            //     if(!arr_Promotion.find(f=> f.name != i.name)) {
                            //         arr_Promotion.push(i)
                            //     }
                            // })
                            let text_arrPromotion = totalPromotion.map(i=>i.name +` (${formatMoneyVN(i.valueAmount||i.amount||i.value)})`+`${i.type =='percent'?` - [${i.value}%]`:''}`).join(', ')
                            let sum1 = scholarshipsA.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            let sum2 = arr_promotion.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            let sum3 = promotionA.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            // if(item.studentCode == '2504700131') {
                            //     console.log('scholarshipsA',scholarshipsA)
                            //     console.log('arr_promotion',arr_promotion)
                            //     console.log('promotionA',promotionA)
                            //     console.log('sum3sum3sum3',sum1+sum2+sum3)
                            // }
                            
                            return {
                                ...item,
                                status: this.statusTag[item.status]?.label,
                                createdAt: new Date(item.createdAt).toLocaleDateString('vi-VN'),
                                updatedAt: new Date(item.updatedAt).toLocaleDateString("vi-VN"),
                                profileCode: item.admissionRegistration?.profileCode,
                                fullName: (item.familyName +' '+item.middleName + ' ' + item.givenName).replace(/\s+/g,' ').trim().replace(/(^|\s)\S/g, (l) => l.toUpperCase()),
                                adMajorName: item.admissionRegistration?.adMajorName,
                                lastModifiedBy:item.lastModifiedBy ? String(item.lastModifiedBy).split("_")[0] : "",
                                programtype: programType[item.programtype],
                                tuition: item.tuitionRegistration?.value||'',
                                scholarships: scholarships.name,
                                scholarshipsA: scholarships.value,
                                promotion: text_arrPromotion + (svsl ? ((text_arrPromotion ? ', ' : '') + svsl.name + ` (${formatMoneyVN(svsl.amount||svsl.value)})`) : ''),
                                promotionA: (sum1+sum2+sum3) + (svsl ? +(svsl.amount||svsl.value||0) : 0),
                                // Tổng giảm trừ = Giảm trừ HB (scholarships.value) + Giảm trừ ưu đãi (sum1+sum2+sum3 + SVSL)
                                totalReduction: (+(scholarships.value)||0) + (sum1+sum2+sum3) + (svsl ? +(svsl.amount||svsl.value||0) : 0),
                                totalpaid: +item.totalpaid,
                                totalremaining: item.totalremaining != null && Number.isFinite(+item.totalremaining) ? +item.totalremaining : calcRemainingFallback(item),
                                tuitionRegistration: item.tuitionRegistration?.name,
                                assignee: item.Student?.User?.fullName,
                                isApplicationFormPrinted: item.isApplicationFormPrinted?"Đã TNHS":"",
                                hasDeposit: item.isDeposit ? "Có" : "",
                                hasTuition: item.isTuition ? "Có" : "",
                                open: item.open?"":"Đã chuyển giao"
                            }
                })
                const keys = Object.keys(header)
                const rows = items.map(o => keys.map(k => APPLICANT_XLSX_MONEY_KEYS.has(k) ? (Number.isNaN(o[k]) ? '' : formatMoneyVN(o[k])) : (o[k] ?? '')))
                exportStyledXlsx({
                    title: 'DANH SÁCH HỒ SƠ NHẬP HỌC (CHI TIẾT ƯU ĐÃI)',
                    summary: `Tổng: ${rows.length} hồ sơ`,
                    sheetName: 'Hồ sơ nhập học',
                    fileBaseName: 'hosoungviennhaphoc-chitiet',
                    columns: buildApplicantXlsxColumns(header),
                    rows,
                })
            } catch (error) {
                console.log('err', error)
                Swal.fire({
                    text: 'Không thể xuất file Excel.',
                    icon: 'error',
                    buttonsStyling: false,
                    confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            }
        },
        // Bản "Xuất Excel" dùng chung cho các phòng ban — bộ cột mặc định, KHÔNG sửa theo yêu cầu
        // riêng của một phòng (phòng nào cần khác thì thêm bản riêng như exportFileTCKT bên dưới).
        exportFile() {
            const header = {
                createdAt: 'Ngày tạo',
                phase: "Đợt",
                updatedAt: 'Ngày cập nhật',
                studentCode: "MSSV",
                fullName: "Họ tên",
                //Địa chỉ cư trú theo đơn vị hành chính 2 CẤP (không còn Quận/Huyện) — thêm để rà soát dữ liệu
                permanentAddress: "Địa chỉ cư trú",
                permanentWard: "Phường/Xã",
                permanentProvince: "Tỉnh/TP",
                contactMobile: "SĐT",
                contactEmail: "Email",
                email: "Email UMT",
                idNumber: "CCCD",
                highschoolName:"Trường lớp 12",
                adMajorName: "Ngành nhập học",
                programtype: "Loại đào tạo",
                status: "Trạng thái",
                isApplicationFormPrinted: "TNHS",
                open: "Chuyển giao",
                lastModifiedBy: "Chỉnh sửa bởi",
                assignee: "Người phụ trách",
                hasDeposit: "Đóng phí giữ suất",
                hasTuition: "Đóng học phí",
                tuitionRegistration:"Số kỳ đóng phí",
                tuition: "Học phí",
                scholarships: "Học bổng",
                scholarshipsA: "Giảm trừ học bổng",
                svsl: "SVSL",
                svslA: "Giảm trừ SVSL",
                promotion: "Ưu đãi",
                promotionA: "Giảm trừ Ưu đãi",
                totalpaid: "Số tiền đã đóng",
                totalremaining: "Còn lại",

            }
            this.exportApplicantXlsx(header, {
                title: 'DANH SÁCH HỒ SƠ NHẬP HỌC',
                sheetName: 'Hồ sơ nhập học',
                fileBaseName: 'hosoungviennhaphoc',
            })
        },
        // Bản riêng của PHÒNG TÀI CHÍNH - KẾ TOÁN (yêu cầu 2026-08-10): bỏ SĐT + Email UMT,
        // thêm địa chỉ cư trú (Địa chỉ / Phường-Xã / Tỉnh-TP) để làm hồ sơ thu chi.
        // Địa chỉ lấy theo THƯỜNG TRÚ (permanent*, đã chuyển 2 cấp nên không còn Quận/Huyện).
        // Gate bằng quyền riêng EXPORT_TCKT trên feature applicant -> cấp ở trang Phân quyền.
        exportFileTCKT() {
            const header = {
                createdAt: 'Ngày tạo',
                phase: "Đợt",
                updatedAt: 'Ngày cập nhật',
                studentCode: "MSSV",
                fullName: "Họ tên",
                permanentAddress: "Địa chỉ cư trú",
                permanentWard: "Phường/Xã",
                permanentProvince: "Tỉnh/TP",
                contactEmail: "Email",
                idNumber: "CCCD",
                highschoolName:"Trường lớp 12",
                adMajorName: "Ngành nhập học",
                programtype: "Loại đào tạo",
                status: "Trạng thái",
                isApplicationFormPrinted: "TNHS",
                open: "Chuyển giao",
                lastModifiedBy: "Chỉnh sửa bởi",
                assignee: "Người phụ trách",
                hasDeposit: "Đóng phí giữ suất",
                hasTuition: "Đóng học phí",
                tuitionRegistration:"Số kỳ đóng phí",
                tuition: "Học phí",
                scholarships: "Học bổng",
                scholarshipsA: "Giảm trừ học bổng",
                svsl: "SVSL",
                svslA: "Giảm trừ SVSL",
                promotion: "Ưu đãi",
                promotionA: "Giảm trừ Ưu đãi",
                totalpaid: "Số tiền đã đóng",
                totalremaining: "Còn lại",
            }
            this.exportApplicantXlsx(header, {
                title: 'DANH SÁCH HỒ SƠ NHẬP HỌC (P.TCKT)',
                sheetName: 'Hồ sơ nhập học',
                fileBaseName: 'hosoungviennhaphoc-tckt',
            })
        },
        // Dữ liệu dùng CHUNG cho mọi bản xuất của trang — cột nào ra file là do keys của `header`
        // quyết định. Để chung một chỗ vì phần tính học bổng/ưu đãi/còn lại dễ lệch nếu copy 2 bản.
        exportApplicantXlsx(header: Record<string, string>, opts: any) {
            let data = this.tableData as any //await this.funcReturnDataInit()
            let programType = {
                "TIENG_VIET":"Tiếng Việt",
                "SONG_NGU":"Song Ngữ",
            }
            try {
                const items = data.map(item => {
                            let freeTuitionRegistration = item.freeTuitionRegistration||[]
                            let orderDetails = item.orderDetails||{}
                            let scholarships = freeTuitionRegistration.find(i=> i.registrationType == 'scholarships')?.scholarships||{}
                            let scholarshipsA = [] as any

                            let promotion = orderDetails.promotion
                            let promotionAddition = orderDetails.promotionAddition
                            let arr_promotion = [] as any
                            //array promotion
                            let otherPromotions = orderDetails.otherPromotions
                            let promotionMulti = orderDetails.promotionMulti
                            let svsl = null as any
                            if(otherPromotions) {
                                arr_promotion = arr_promotion.concat(otherPromotions)
                            }
                            if(promotionMulti) {
                                arr_promotion = arr_promotion.concat(promotionMulti)
                            }
                            if(promotion) {
                                if(promotion.code == 'SVSL') {
                                    svsl = promotion
                                }
                                else {
                                    arr_promotion.push(promotion)
                                }
                            }
                            if(promotionAddition) {
                                arr_promotion.push(promotionAddition)
                            }
                            let promotionA = [] as any
                            let arr_scholarshipA = ['promotion','promotionExtend']
                            let arr_promotionA = ['promotionvip','promotionvippro']
                            freeTuitionRegistration.forEach(i=> {
                                if(i.registrationType == 'scholarshipPromotionMultiData') {
                                    let data = i[i.registrationType]
                                    scholarshipsA = scholarshipsA.concat(data)
                                }
                                if(arr_scholarshipA.includes(i.registrationType)) {
                                    let name = i[i.registrationType].name
                                    if(name) {
                                        scholarshipsA.push(i[i.registrationType])   
                                    }
                                }
                                if(arr_promotionA.includes(i.registrationType)) {
                                    let name = i[i.registrationType].name
                                    if(name) {
                                        promotionA.push(i[i.registrationType])   
                                    }
                                }
                            })
                            let text_arrPromotion = scholarshipsA.concat(arr_promotion.concat(promotionA)).map(i=>i.name)?.join(', ')
                            let sum1 = scholarshipsA.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            let sum2 = arr_promotion.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            let sum3 = promotionA.reduce(function (acc, obj) { return acc + +(obj.valueAmount||obj.amount||obj.value) }, 0);
                            // if(item.studentCode == '2504700131') {
                            //     console.log('scholarshipsA',scholarshipsA)
                            //     console.log('arr_promotion',arr_promotion)
                            //     console.log('promotionA',promotionA)
                            //     console.log('sum3sum3sum3',sum1+sum2+sum3)
                            // }
                            
                            return {
                                ...item,
                                status: this.statusTag[item.status]?.label,
                                createdAt: new Date(item.createdAt).toLocaleDateString('vi-VN'),
                                updatedAt: new Date(item.updatedAt).toLocaleDateString("vi-VN"),
                                profileCode: item.admissionRegistration?.profileCode,
                                fullName: (item.familyName +' '+item.middleName + ' ' + item.givenName).replace(/\s+/g,' ').trim().replace(/(^|\s)\S/g, (l) => l.toUpperCase()),
                                adMajorName: item.admissionRegistration?.adMajorName,
                                lastModifiedBy:item.lastModifiedBy ? String(item.lastModifiedBy).split("_")[0] : "",
                                programtype: programType[item.programtype],
                                tuition: item.tuitionRegistration?.value||'',
                                scholarships: scholarships.name,
                                scholarshipsA: scholarships.value,
                                svsl: svsl?.name,
                                svslA: svsl?.amount,
                                promotion: text_arrPromotion,
                                promotionA: sum1+sum2+sum3,
                                totalpaid: +item.totalpaid,
                                totalremaining: item.totalremaining != null && Number.isFinite(+item.totalremaining) ? +item.totalremaining : calcRemainingFallback(item),
                                tuitionRegistration: item.tuitionRegistration?.name,
                                assignee: item.Student?.User?.fullName,
                                isApplicationFormPrinted: item.isApplicationFormPrinted?"Đã TNHS":"",
                                hasDeposit: item.isDeposit ? "Có" : "",
                                hasTuition: item.isTuition ? "Có" : "",
                                open: item.open?"":"Đã chuyển giao"
                            }
                })
                const keys = Object.keys(header)
                const rows = items.map(o => keys.map(k => APPLICANT_XLSX_MONEY_KEYS.has(k) ? (Number.isNaN(o[k]) ? '' : formatMoneyVN(o[k])) : (o[k] ?? '')))
                exportStyledXlsx({
                    title: opts.title,
                    summary: `Tổng: ${rows.length} hồ sơ`,
                    sheetName: opts.sheetName,
                    fileBaseName: opts.fileBaseName,
                    columns: buildApplicantXlsxColumns(header),
                    rows,
                })
            } catch (error) {
                console.log('err', error)
                Swal.fire({
                    text: 'Không thể xuất file Excel.',
                    icon: 'error',
                    buttonsStyling: false,
                    confirmButtonText: 'Đóng',
                    customClass: { confirmButton: 'btn btn-primary' },
                })
            }
        },
        async funcReturnDataInit() {
            // Nghiệp vụ chốt 2026-07-16: trang Danh sách nhập học KHÔNG lọc theo role —
            // mọi nhân viên đều thấy toàn bộ hồ sơ (mùa nhập học ai cũng tiếp nhận được).
            // (Trước đây role != admin chỉ thấy hồ sơ mình/nhóm mình phụ trách.)
            let {data} = await axios.get(`applicant/assignee/list`)
            data = data.map(item=>{
                let _receipts = item._receipts||[]
                _receipts = _receipts.filter(i=> i.items[0] && !i.items[0]?.isreversed)
                let isTuition = !!_receipts.find(i=> i.items[0]?.type == 'Tuition Fee')
                let isDeposit = !!_receipts.find(i=> i.items[0]?.type == 'Deposit')
                if(item.status == "cancel") {
                    isTuition = false
                    isDeposit = false
                }
                return {
                    ...item,
                    isTuition: isTuition,
                    isDeposit: isDeposit,
                }
            })
            return data
        },
        async getData() {
            this.loading = true
            this.tableData = []
            this.initCustomers = []
            this.initCustomersFIlter = []
            let data = await this.funcReturnDataInit()
            // await axios.get(`applicant/assignee/list${params}`)
                // .then(({ data }) => {
                    //this.pagination.total = data.itemCount
                    //this.pagination.currentPage = page
                    data.sort((a,b)=> Number(new Date(b.updatedAt)) - Number(new Date(a.updatedAt)))
                    // Nằm dưới label "Người phụ trách" ở panel Lọc nâng cao nên option mặc định là "Tất cả"
                    this.optionAssignee = [
                        {
                            label: 'Tất cả',
                            value: -1
                        }
                    ] as any

                    data.forEach((item, idx) => {
                        // ai cũng lọc được theo người phụ trách (đã bỏ lọc role toàn trang)
                        {
                            if(item.Student && item.Student.User) {
                                if(!this.optionAssignee.find((i: any) => i.value == item.Student.assignee)) {
                                    this.optionAssignee.push({
                                        label: item.Student.User.fullName,
                                        value: item.Student.assignee,
                                    })
                                }

                            }
                            if(item.Student && item.Student.Group) {
                                if(!this.optionAssignee.find((i: any) => i.value == item.Student.groupAssignee)) {
                                    this.optionAssignee.push({
                                        label: item.Student.Group.groupName,
                                        value: item.Student.groupAssignee,
                                    })
                                }

                            }
                        }
                        if(item.phase && !this.arrPhase.includes(item.phase)) {
                            this.arrPhase.push(item.phase)
                        }
                        let _receipts = item._receipts||[]
                        _receipts = _receipts.filter(i=> i.items[0] && !i.items[0]?.isreversed)
                        let isTuition = !!_receipts.find(i=> i.items[0]?.type == 'Tuition Fee')
                        let isDeposit = !!_receipts.find(i=> i.items[0]?.type == 'Deposit')
                        
                        let scholarshipCode = item.freeTuitionRegistration?.find(i => i.registrationType == 'scholarships')?.scholarships?.code||''
                        if(item.status == "cancel") {
                            isTuition = false
                            isDeposit = false
                            scholarshipCode = ''
                        }
                        // Điểm xét tuyển (BE đính adScore từ admission_winners) — format sẵn 1 chuỗi để sort/search/copy-deep an toàn.
                        // Cùng convention admissionResults: điểm thang <13 (học bạ/THPT) làm tròn 2 số; PT không điểm số → passedUmtCondition
                        let adScoreText = ''
                        if (item.adScore) {
                            const s = item.adScore
                            adScoreText = s.averageScore
                                ? (s.averageScore < 13 ? (+s.averageScore).toFixed(2) : String(s.averageScore))
                                : (s.passedUmtCondition || '')
                        }
                        let temp = {
                            ...item,
                            id: item.id,
                            studentId: item.studentId,
                            fullName: (item.familyName +' '+item.middleName + ' ' + item.givenName).replace(/\s+/g,' ').trim().replace(/(^|\s)\S/g, (l) => l.toUpperCase()),
                            major: item.major,
                            adScoreText: adScoreText,
                            idNumber: item.idNumber,
                            phase: item.phase,
                            code: item.admissionRegistration.profileCode,
                            status: item.status,
                            createdAt: Number(new Date(item.createdAt)),
                            createdAtFormat: moment(item.createdAt).format('DD/MM/YYYY'),
                            updatedAt: item.updatedAt?Number(new Date(item.updatedAt)):'',
                            updatedAtFormat: item.updatedAt?moment(item.updatedAt).format('DD/MM/YYYY'):'',
                            studentCode: item.studentCode,
                            lastModifiedBy: item.lastModifiedBy,
                            isApplicationFormPrinted: item.isApplicationFormPrinted,
                            paidBy: item.paidBy != 'none'? 'Đã thanh toán' : '',
                            Student: item.Student,
                            isTuition: isTuition,
                            isDeposit: isDeposit,
                            scholarshipCode: scholarshipCode,

                        }
                        this.tableData.push(temp  as any)
                    })
                    this.initCustomers.splice(0, this.tableData.length, ...this.tableData);
                    this.initCustomersFIlter.splice(0, this.tableData.length, ...this.tableData);
                    //Áp ngay bộ lọc mặc định (ẩn hồ sơ đã hủy) — nạp xong mà không lọc thì
                    //lần đầu vào trang vẫn thấy đủ cả hồ sơ hủy dù ô Tình trạng đang chọn 3.
                    this.applyFilter()

                    this.loading = false
                    this.arrPhase = this.arrPhase.sort((a,b)=> Number(a) - Number(b))

                // })
                // .catch(({ response }) => {
                //     console.log('err', response)
                // });
        },
        currentChange(currentPage, limit = 0) {
            if (limit == 0) {
                limit = this.pagination.limit
            }
            else {
                this.pagination.limit = limit
            }
            //this.getData()
        }
    },
    async created() {

        this.getData()
        await axios.get(`/setting/scholarship`)
        .then(({ data }) => {
            if(data && data.length != 0) {
                this.optionScholarship = data
            }
        })
        .catch(({ response }) => {
            console.log('err', response)
        });
    },
    setup() {
        // Nhớ trạng thái mở/đóng "Lọc nâng cao" theo trang (cùng key-convention với FilterBar); mặc định ĐÓNG cho gọn
        const ADV_KEY = 'umt_filterbar_adv_' + window.location.pathname
        const _savedAdv = localStorage.getItem(ADV_KEY)
        const showAdvanced = ref(_savedAdv === '1')
        watch(showAdvanced, (v) => { try { localStorage.setItem(ADV_KEY, v ? '1' : '0') } catch (e) { /* storage đầy/ẩn danh — bỏ qua */ } })
        const formData = ref<any>({
            isApproved: -1,
            paid: -1,
            phase: -1,
            status: [...DEFAULT_STATUS],
            assignee: -1,
            scholarship: []
        })
        const userLocal = computed(() => {
            let temp = window.localStorage.getItem("user")
            return temp ? JSON.parse(temp) : null
        });
        const isSuperAdmin = computed(() => {
        let infUser = userLocal.value
        if(infUser.role == 1 || infUser.role == 3) {
            return true
        }
        return false
        });
        //CHỈ Superadmin thật (role id 1) — khác isSuperAdmin ở trên vốn gộp cả admin(3).
        //Dùng cho "Xóa phiếu nhập học": thao tác XÓA CỨNG không hoàn tác (BE cũng gác requireSuperAdmin).
        const isOnlySuperAdmin = computed(() => String(userLocal.value?.role) === '1');
        //Quyền dạng HÀM (v-if) thay cho directive v-permission ở cụm Xuất Excel: cần kết hợp OR nhiều quyền
        //(kebab hiện khi có EXPORT HOẶC EXPORT_TCKT) mà directive chỉ xét được 1 mã.
        //Quyền đọc từ localStorage 'user' — chỉ nạp lúc ĐĂNG NHẬP, đổi phân quyền phải đăng xuất/nhập lại.
        const hasApplicantPerm = (code: string) => {
            if (isSuperAdmin.value) return true //role 1/3 luôn full (đồng bộ checkRoleIsAdmin của directive)
            const feature = (userLocal.value?.permissions || []).find((i: any) => i.name == Permission.APPLICANT)
            return Array.isArray(feature?.permissions) && feature.permissions.includes(code)
        }
        const canExport = computed(() => hasApplicantPerm(Permission.APPLICANT_EXPORT))
        const canExportTCKT = computed(() => hasApplicantPerm(Permission.APPLICANT_EXPORT_TCKT))
        const arrPhase = ref<any>([-1])
        const optionAssignee = ref<any>([])
        const optionScholarship = ref<any>([])
            
        const pagination = ref<IPagination>({
            currentPage: 1,
            total: 0,
            limit: 100,
        });
        const checkedCustomers = ref([] as any);
        const checkDefault = ref(false)
        const callFunctionModal = ref<any>();
        // Thứ tự: nhận diện (Họ tên, MSV) -> Ngành -> meta (Đợt, ngày) -> tiến độ (Tình trạng, TNHS, phí) ở CUỐI
        // -> Phụ trách (concat theo role ở onMounted) -> icon chi tiết (concat cuối cùng)
        let tableHeader = ref<any>([
                {
                key: "checkbox",
                sortable: false,
                class:"d-flex justify-content-center",
                method: "checkall",
            },
            {
                name: "MÃ HỒ SƠ",
                key: "code",
                sortable: true,
                class: "w-90px text-nowrap"
            },
            {
                name: "HỌ TÊN / MSSV",
                key: "fullName",
                sortable: true,
                class: "min-w-150px"
            },
            {
                name: "NGÀNH NHẬP HỌC",
                key: "major",
                sortable: true,
                class: "min-w-200px",
            },
            {
                name: "HỌC BỔNG",
                key: "scholarshipName",
                sortable: false,
                class: "text-start",
            },
            {
                name: "NGÀY TẠO / SỬA",
                key: "createdAt",
                sortable: true,
                class: "min-w-100px text-nowrap"
            },
            {
                name: "PHỤ TRÁCH",
                key: "assigneeName",
                sortable: false,
                class: "min-w-100px text-nowrap",
            },
            {
                name: "TNHS",
                key: "isApplicationFormPrinted",
                sortable: true,
                class: "text-center",
            },
            {
                name: "Đóng PGS",
                key: "isDeposit",
                sortable: false,
                class: "text-center text-nowrap",
            },
            {
                name: "Đóng HP",
                key: "isTuition",
                sortable: false,
                class: "text-center text-nowrap",
            },
            {
                name: "TÌNH TRẠNG",
                key: "status",
                sortable: true,
                class: "text-end min-w-100px text-nowrap",
            },
        ]);
        
        const tableData = ref<Array<ICustomer>>([]);
        const initCustomers = ref<Array<ICustomer>>([]);
        const initCustomersFIlter = ref<Array<ICustomer>>([]);
        const statusTag = {
            "completed": {
                label: "Chờ xác thực",
                state: "warning",
            },
            "confirmed": {
                label: "Hoàn tất",
                state: "success",
            },
            "processing": {
                label: "Chưa hoàn tất",
                state: "warning",
            },
            "cancel": {
                label: "Đã hủy",
                state: "danger",
            }
        }
        //THỐNG KÊ dải trên bảng: đếm trên initCustomersFIlter = danh sách ĐANG LỌC (không phải toàn hệ),
        //nên đổi bộ lọc/tìm kiếm là số tự đổi theo. Trạng thái nào không có hồ sơ thì ẩn chip.
        //Hồ sơ ĐÃ HỦY không phải ứng viên đang nhập học -> loại khỏi MỌI con số ở dải này
        //(cả tổng, chip tình trạng lẫn đếm tiếp nhận/phí), không hiện chip "Đã hủy" nữa.
        const STATUS_ORDER = ['processing', 'completed', 'confirmed']
        const stats = computed(() => {
            const allRows: any[] = initCustomersFIlter.value || []
            const rows = allRows.filter((r: any) => r.status !== 'cancel')
            return {
                rows: allRows.length, //tổng dòng đang lọc — chỉ dùng để quyết định có hiện dải hay không
                total: rows.length,
                byStatus: STATUS_ORDER
                    .map((key) => ({
                        key,
                        label: (statusTag as any)[key]?.label || key,
                        state: (statusTag as any)[key]?.state || 'primary',
                        n: rows.filter((r: any) => r.status === key).length,
                    }))
                    .filter((s) => s.n),
                tnhs: rows.filter((r: any) => r.isApplicationFormPrinted).length,
                pgs: rows.filter((r: any) => r.isDeposit).length,
                hp: rows.filter((r: any) => r.isTuition).length,
            }
        })
        const statusPaid = {
            "bank_transfers": {
                label: "Chuyển khoản",
                state: "primary",
            },
            "atm_card": {
                label: "ATM/VISA/MASTERCARD",
                state: "primary",
            },
            "cash": {
                label: "Tiền mặt",
                state: "primary",
            },
            "none": {
                label: "",
                state: "info",
            },
        }
        onMounted(async () => {
        // Cột icon mở trang chi tiết (title trống) — concat sau cùng để luôn nằm cuối bảng
        tableHeader.value = tableHeader.value.concat([
            {
                name: "",
                key: "goDetail",
                sortable: false,
                class: "text-end",
            },
        ])
        });
        const search = ref<string>("");
        const searchItems = () => {
            tableData.value.splice(0, tableData.value.length, ...initCustomersFIlter.value);
            if (search.value !== "") {
                let results: Array<ICustomer> = [];
                for (let j = 0; j < tableData.value.length; j++) {
                    if (searchingFunc(tableData.value[j], search.value)) {
                        results.push(tableData.value[j]);
                    }
                }
                tableData.value.splice(0, tableData.value.length, ...results);
            }
        };
        const searchingFunc = (obj, value): boolean => {
            obj = JSON.parse(JSON.stringify(obj))
            obj.status = statusTag[obj.status]?.label
            for (let key in obj) {
                if (obj[key] && (typeof obj[key] === "string")) {
                    if (obj[key].toUpperCase().indexOf(value.toUpperCase()) != -1) {
                        return true;
                    }
                }
            }
            return false;
        };

        return {
            tableData,
            initCustomers,
            initCustomersFIlter,
            tableHeader,
            showAdvanced,
            search,
            checkedCustomers,
            pagination,
            statusTag,
            statusPaid,
            searchItems,
            Permission,
            checkDefault,
            callFunctionModal,
            formData,
            arrPhase,
            userLocal,
            optionAssignee,
            optionScholarship,
            isSuperAdmin,
            isOnlySuperAdmin,
            canExport,
            canExportTCKT,
            stats,
            canViewCandidateDetail
        };
    },
});
